import { useState } from 'react';
import { ChevronDownIcon, PhoneIcon, EnvelopeIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { useContent } from '../context/ContentContext';

export default function FAQ() {
  const { content, settings } = useContent();
  const faq = content.faq;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-16 sm:py-20 lg:py-24 bg-brand-off-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-orange/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-brand-navy/5 rounded-full blur-3xl" />

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative min-w-0">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20 items-start min-w-0">
          <div className="lg:col-span-5 min-w-0">
            <div className="lg:sticky lg:top-28 xl:top-48">
              <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-brand-orange rounded-full" />
                <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                  {faq.sectionLabel}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
                {faq.heading}
              </h2>

              <p className="text-base sm:text-lg text-[#756c65] leading-relaxed mb-8 sm:mb-10">
                {faq.description}
              </p>

              <div className="bg-white rounded-2xl p-6 sm:p-7 border border-gray-100 mb-6">
                <h3 className="font-bold text-brand-navy text-lg mb-3 sm:mb-4">{faq.stillHaveQuestionsHeading}</h3>
                <p className="text-brand-warm-gray text-sm leading-relaxed mb-5 sm:mb-6">
                  {faq.stillHaveQuestionsText}
                </p>

                <div className="space-y-3">
                  <a
                    href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                    className="group flex items-center gap-3 bg-brand-navy text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-lg font-semibold hover:bg-brand-orange transition-colors duration-300 w-full"
                  >
                    <PhoneIcon className="w-5 h-5" />
                    <span>{settings.phone}</span>
                    <ArrowRightIcon className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-transform" />
                  </a>
                  <a
                    href={`mailto:${settings.email}`}
                    className="flex items-center gap-3 bg-brand-off-white text-brand-navy px-5 sm:px-6 py-3.5 sm:py-4 rounded-lg font-semibold hover:bg-brand-orange/10 transition-colors duration-300 w-full"
                  >
                    <EnvelopeIcon className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    <span className="text-sm truncate">{settings.email}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 min-w-0">
            <div className="space-y-3">
              {faq.faqs.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                  <div
                    key={index}
                    className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                      isOpen
                        ? 'border-brand-orange/20 shadow-lg'
                        : 'border-gray-100 hover:border-brand-orange/20 hover:shadow-md'
                    }`}
                  >
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${index}`}
                      className="w-full flex items-start gap-3 sm:gap-4 p-5 sm:p-7 text-left"
                    >
                      <span className={`text-xs font-bold mt-1 flex-shrink-0 transition-colors duration-300 ${
                        isOpen ? 'text-brand-orange' : 'text-[#787674]'
                      }`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="font-semibold text-brand-navy leading-snug flex-1 pr-2 text-[15px] sm:text-base">
                        {item.question}
                      </span>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        isOpen
                          ? 'bg-brand-orange rotate-180'
                          : 'bg-brand-off-white'
                      }`}>
                        <ChevronDownIcon className={`w-4 h-4 transition-colors duration-300 ${
                          isOpen ? 'text-white' : 'text-brand-warm-gray'
                        }`} />
                      </div>
                    </button>
                    <div
                      id={`faq-answer-${index}`}
                      aria-hidden={!isOpen}
                      className={`grid transition-all duration-300 ${
                        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="px-5 sm:px-7 pb-5 sm:pb-7 pl-[44px] sm:pl-[60px]">
                          <p className="text-brand-warm-gray leading-relaxed text-sm sm:text-[15px]">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
